<template>
  <BaseConfirmationCard
    :width="width"
    :cancel-button-text="null"
    confirm-button-text="Generate test"
    :on-close="onClose"
    :on-confirm="handleSubmit(handleGenerate)"
    paddingless
  >
    <template #title>Generate a new test</template>
    <div class="flex flex-col gap-6">
      <div
        v-if="filteredTestTypes.length > 1"
        class="w-full flex px-6 gap-6"
      >
        <BaseRadio
          v-for="option in filteredTestTypes"
          :key="option.value"
          v-model="selectedTestType"
          :label="option.label"
          :val="option.value"
          :checked="option.value === selectedTestType"
          :outline="false"
          class="hover:!bg-transparent !p-0 !shadow-none active:!shadow-none"
          data-testid="test-type-radio"
        ></BaseRadio>
      </div>
      <BaseInputDropdown
        v-if="selectedTestType === 'codility'"
        v-model="selectedTest"
        name="Codility Test"
        :options="codilityTests"
        map-options
        type="search"
        placeholder="Search for a test topic or skill"
        data-testid="codility-test-dropdown"
        class="px-6 pb-1"
      />
      <BaseInputField
        v-else-if="selectedTestType === 'english'"
        name="English Test"
        placeholder="SmallTalk2Me"
        data-testid="english-test-input"
        class="px-6 pb-1"
        disable
      />
    </div>
  </BaseConfirmationCard>
</template>
<script>
import { ref, inject, computed, toRefs } from 'vue';
import { useForm } from 'vee-validate';
import {
  BaseInputDropdown,
  BaseConfirmationCard,
  BaseInputField,
} from '@/components/Base';
import { useStore } from 'vuex';
import BaseRadio from '../../Base/BaseRadio.vue';
import { useTestGenerator } from '@/composables/useTestGenerator';

export default {
  components: {
    BaseInputField,
    BaseRadio,
    BaseInputDropdown,
    BaseConfirmationCard,
  },
  props: {
    width: {
      type: String,
      required: false,
      default() {
        return '400px';
      },
    },
    applicationId: {
      type: [String, Number],
      required: true,
    },
    menuPlacement: {
      type: String,
      required: false,
      default() {
        return 'right-start';
      },
    },
    onGenerateTest: {
      type: Function,
      required: false,
      default: () => () => {},
    },
    onClose: {
      type: Function,
      required: false,
      default: () => () => {},
    },
    displayTestTypes: {
      type: Array,
      required: false,
      default() {
        return ['codility', 'english'];
      },
      validator(value) {
        return value.every((type) => ['codility', 'english'].includes(type));
      },
    },
    currentStage: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    skipStageValidation: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  setup(props) {
    const codilityTests = inject('codilityTests');
    const store = useStore();
    const { handleSubmit } = useForm();
    const selectedTest = ref(null);
    const generateTestCard = ref(null);
    const testTypes = ref([
      { label: 'Codility', value: 'codility' },
      { label: 'English Assessment', value: 'english' },
    ]);

    const { currentStage } = toRefs(props);

    const filteredTestTypes = computed(() => {
      return testTypes.value.filter((testType) =>
        props.displayTestTypes.includes(testType.value)
      );
    });
    const selectedTestType = ref(
      filteredTestTypes.value.find((type) => type.value === 'codility')
        ? 'codility'
        : 'english'
    );

    const defaultTestType = computed(() => {
      return filteredTestTypes.value.length > 0
        ? filteredTestTypes.value[0].value
        : 'codility';
    });

    if (
      filteredTestTypes.value.length > 0 &&
      !props.displayTestTypes.includes(selectedTestType.value)
    ) {
      selectedTestType.value = defaultTestType.value;
    }

    const { generateTest } = useTestGenerator({
      store,
      onGenerateTest: props.onGenerateTest,
      onClose: props.onClose,
    });

    const handleGenerate = async () => {
      if (selectedTestType.value === 'english') {
        if (!props.skipStageValidation && currentStage.value?.id !== 10) {
          store.commit('ui/addSnackbarMessage', {
            title: 'The application is not in the English Assessment stage!',
            type: 'error',
          });
          return;
        }
        await generateTest('english', {
          applicationId: props.applicationId,
        });
      } else {
        if (!props.skipStageValidation && currentStage.value?.id !== 4) {
          store.commit('ui/addSnackbarMessage', {
            title: 'The application is not in the Technical Assessment stage!',
            type: 'error',
          });
          return;
        }
        await generateTest('codility', {
          testId: selectedTest.value.id,
          applicationId: props.applicationId,
          testName: selectedTest.value.name,
        });
      }
    };

    const handleChange = (value) => {
      selectedTest.value = value;
    };

    return {
      testTypes,
      filteredTestTypes,
      generateTestCard,
      handleChange,
      handleSubmit,
      codilityTests,
      selectedTest,
      selectedTestType,
      handleGenerate,
      defaultTestType,
    };
  },
};
</script>
